import { Alert, Box, Button, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import { GridRowsProp } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import Breadcrumbs from 'components/breadcrumbs';
import Error from 'components/error';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';

import TextField from 'components/text-field';
import ToggleButton from 'components/toggle-button';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import Mui from 'materials';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { OwnerType } from 'types/shared/owner-type.enum';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import FeedStrategiesGrid from 'views/profiles-strategies/features/feed-strategies/components/feed-strategies-data-grid';
import { UpdateFeedStrategyInput } from 'views/profiles-strategies/features/feed-strategies/types/update-feed-strategy-input.type';
import ProfilesStrategiesTabHeader from '../../../components/profile-strategies-tabs-header';
import { getFeedStrategy } from '../api/get-feed-strategy';
import { getProductsArticles, getProductsCustomerAssortment } from '../api/get-products';
import { putFeedStrategy } from '../api/update-feed-strategy';
import { checkDuplicateProfiles, checkStartWeight } from '../utils/strategy-validation';

type ParamTypes = {
  id: 'string';
};

type FeedStrategies = {
  fromWeight: number;
  product: { id: string; label: string | undefined };
};

type ProductFiltered = {
  id: string;
  label: string;
};

type Row = {
  id: string;
  fromWeight: number;
  product: { id: string; label: string };
};

type Props = {};

const EditFeedStrategies: React.FC<Props> = () => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const { errorDictionary, changeErrorDictionary, handleEditing, isEditing, hasError } = useDataGridError();

  const [rows, setRows] = useState<GridRowsProp<Row>>([]);
  const [duplicateLines, setDuplicateLines] = useState<boolean>(false);
  const [startWeightError, setStartWeightError] = useState<boolean>(false);
  const [feedStrategyLines, setFeedStrategyLines] = useState<FeedStrategies[]>([]);

  // FORMS
  const { handleSubmit, control, reset, getValues } = useForm<UpdateFeedStrategyInput>({
    defaultValues: {
      name: '',
      active: false,
      isTemporary: false,
      lines: [],
    },
    shouldUnregister: false,
  });

  // FETCH DATA
  const {
    data: feedStrategy,
    error,
    isError,
    isFetching,
  } = useGet(['getFeedStrategy', params.id], (context) => getFeedStrategy(String(context.queryKey[1])), {
    enabled: !!params.id,
  });

  const { data: products } = useGet(
    ['getProductsArticles', feedStrategy?.ownerType, account.company!.customerErpId],
    (context) =>
      context.queryKey[1] === OwnerType.MARKET ? getProductsArticles() : getProductsCustomerAssortment(String(context.queryKey[2])),
    {
      enabled: !!account.company?.customerErpId && !!feedStrategy?.ownerType,
    }
  );

  const productLines = useMemo<ProductFiltered[]>(
    () =>
      products
        ?.filter((p) => p.ItemName !== null)
        .sort((a, b) => a.ItemName.toLowerCase().localeCompare(b.ItemName.toLowerCase()))
        .map((p) => ({
          id: String(p.ItemNumber),
          label: `${p.ItemName} (${Number(p.ItemNumber)})`,
        })) ?? [],
    [products]
  );

  // POST DATA
  const updateFeedStrategy = usePost<AxiosResponse, UpdateFeedStrategyInput>(
    (request) => putFeedStrategy(feedStrategy!.id, request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getFeedStrategy', params.id],
        exact: true
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS
  const canManageProfileStrategy = (owner: OwnerType): boolean => {
    switch (owner) {
      case OwnerType.MARKET:
        return account.company?.features.hasPlanningPrognosisManageMarketProfiles ?? false;
      case OwnerType.COMPANY:
      case OwnerType.FISH_GROUP:
      case OwnerType.SITE:
        return account.company?.features.hasPlanningPrognosisManageCompanyOrSiteProfiles ?? false;
      default:
        return false;
    }
  };

  const handleUpdateFeedStrategy = (request: UpdateFeedStrategyInput) => {
    let data = {
      ...request,
      lines: rows.map((line) => {
        return {
          fromWeight: line.fromWeight,
          productNumber: line.product?.id ?? '',
        };
      }),
    };
    updateFeedStrategy.mutate(data);
  };

  useEffect(() => {
    setStartWeightError(checkStartWeight(rows as Row[]));
    setDuplicateLines(checkDuplicateProfiles(rows as Row[]));
  }, [rows]);

  useEffect(() => {
    if (!feedStrategy) return;

    reset({
      ...getValues(),
      name: feedStrategy.name,
      active: feedStrategy.active,
      isTemporary: feedStrategy.isTemporary,
    });
    setFeedStrategyLines(
      feedStrategy.lines
        .filter((p) => p.product?.name !== null)
        .map((p) => ({ fromWeight: p.fromWeight, product: { id: String(p.product?.productNumber), label: p.product?.name } }))
    );
  }, [feedStrategy, getValues, reset]);

  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      return row.product?.id === '';
    });
  }, [rows]);

  return isError ? (
    <Error error={error} />
  ) : !feedStrategy || isFetching ? (
    <LoadingSpinner />
  ) : (
    <>
      <ProfilesStrategiesTabHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: feedStrategy.name,
                url: String(feedStrategy.id),
              },
            ]}
          />
        }
      />

      <form onSubmit={handleSubmit(handleUpdateFeedStrategy)}>
        <Grid container rowGap={4} spacing={4}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper transparent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" paddingBottom={4}>
                <Typography variant="h3" color={theme.palette.common.white}>
                  <FormattedMessage id={`profilesStrategies.label.name`} />
                </Typography>
              </Box>
              <Paper>
                <TextField
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `general.product` })}
                  disabled={!canManageProfileStrategy(feedStrategy.ownerType)}
                  controller={{
                    name: 'name',
                    control: control,
                    rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                  }}
                />
                <ToggleButton
                  dark
                  disabled={!canManageProfileStrategy(feedStrategy.ownerType)}
                  label={intl.formatMessage({ id: `profilesStrategies.label.feedStrategyActive` })}
                  controller={{
                    name: 'active',
                    control: control,
                  }}
                />
                <ToggleButton
                  dark
                  disabled={!canManageProfileStrategy(feedStrategy.ownerType)}
                  label={intl.formatMessage({ id: `profilesStrategies.label.feedStrategyTemporary` })}
                  controller={{
                    name: 'isTemporary',
                    control: control,
                  }}
                />
              </Paper>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper transparent>
              <Typography mb={4} variant="h3" color={theme.palette.common.white}>
                <FormattedMessage id={`profilesStrategies.ownerTitle`} />
              </Typography>
              <Paper>
                <Mui.FormControl fullWidth>
                  <Typography variant="caption">
                    <FormattedMessage id={`profilesStrategies.menuItem.market`} />
                  </Typography>
                  <Typography>{feedStrategy.ownerMarket}</Typography>
                </Mui.FormControl>
                {feedStrategy.ownerType === OwnerType.COMPANY && (
                  <Mui.FormControl fullWidth>
                    <Typography variant="caption">
                      <FormattedMessage id={`profilesStrategies.menuItem.company`} />
                    </Typography>
                    <Typography>{feedStrategy.ownerCompanyId}</Typography>
                  </Mui.FormControl>
                )}
                {feedStrategy.ownerType === OwnerType.SITE && (
                  <Mui.FormControl fullWidth>
                    <Typography variant="caption">
                      <FormattedMessage id={`profilesStrategies.menuItem.site`} />
                    </Typography>
                    <Typography>{feedStrategy.ownerSiteId}</Typography>
                  </Mui.FormControl>
                )}
                <Mui.FormControl fullWidth>
                  <Typography variant="caption">
                    <FormattedMessage id={`profilesStrategies.menuItem.connectedFishGroups`} />
                  </Typography>
                  <Typography>{feedStrategy.numberOfConnectedFishgroups}</Typography>
                </Mui.FormControl>
              </Paper>
            </Paper>
          </Grid>
          <Grid size={12}>
            {!!duplicateLines && (
              <Alert severity="error">
                <FormattedMessage id={`profilesStrategies.createFeedStrategies.duplicateProfiles`} />
              </Alert>
            )}
            {!!startWeightError && (
              <Alert severity="error">
                <FormattedMessage id={`profilesStrategies.createFeedStrategies.startWeightValue`} />
              </Alert>
            )}
            <Grid size={12}>
              <Paper transparent>
                <Typography mb={4} variant="h2" color={theme.palette.common.white}>
                  <FormattedMessage id={`profilesStrategies.linesTitle`} />
                </Typography>
                <FeedStrategiesGrid
                  rows={rows}
                  setRows={setRows}
                  productLines={productLines}
                  feedStrategy={feedStrategyLines}
                  handleEditing={handleEditing}
                  errorDictionary={errorDictionary}
                  changeErrorDictionary={changeErrorDictionary}
                />
              </Paper>
            </Grid>
          </Grid>
          <Box display="flex" justifyContent="end" mt={4} width={'100%'}>
            <Button
              type="submit"
              disabled={
                !canManageProfileStrategy(feedStrategy.ownerType) ||
                updateFeedStrategy.isPending ||
                !!duplicateLines ||
                !!startWeightError ||
                disableSubmit ||
                hasError ||
                isEditing
              }>
              {updateFeedStrategy.isPending ? (
                <CircularProgress color="inherit" />
              ) : (
                <FormattedMessage id="general.submit" values={{ type: '' }} />
              )}
            </Button>
          </Box>
        </Grid>
      </form>
    </>
  );
};

export default EditFeedStrategies;
